<script setup lang="ts">
import { AlertCircle, Download, Info, Laptop, Monitor, Terminal } from '@lucide/vue'
import {
  ameliaOperatingSystem,
  type AmeliaAsset,
  type AmeliaRelease,
} from '~~/shared/schemas/amelia'

const emit = defineEmits<{ closeMenu: [] }>()
const open = ref(false)
const pending = ref(false)
const error = ref('')
const release = ref<AmeliaRelease>()
const operatingSystem = ref<AmeliaAsset['os']>()
const platforms = computed(() =>
  [
    { os: 'windows' as const, name: 'Windows', icon: Monitor },
    { os: 'macos' as const, name: 'macOS', icon: Laptop },
    { os: 'linux' as const, name: 'Linux', icon: Terminal },
  ]
    .map((platform) => ({
      ...platform,
      assets: release.value?.assets.filter((asset) => asset.os === platform.os) ?? [],
    }))
    .filter((platform) => platform.assets.length)
    .sort(
      (a, b) => Number(b.os === operatingSystem.value) - Number(a.os === operatingSystem.value),
    ),
)
const releaseDate = computed(() => {
  if (!release.value) return ''
  const date = new Date(release.value.releasedAt)
  return Number.isNaN(date.getTime())
    ? ''
    : new Intl.DateTimeFormat('fr-FR', { dateStyle: 'long' }).format(date)
})
function architectureLabel(asset: AmeliaAsset) {
  if (asset.architecture === 'unknown') return 'Installation standard'
  if (asset.os === 'macos')
    return asset.architecture === 'arm64' ? 'Apple Silicon (M1 et suivants)' : 'Intel'
  return asset.architecture === 'arm64' ? 'ARM64' : '64 bits (x64)'
}
async function download(choose = false) {
  if (pending.value) return
  pending.value = true
  error.value = ''
  try {
    release.value = await $fetch<AmeliaRelease>('/api/amelia')
    const os = ameliaOperatingSystem(navigator.userAgent)
    operatingSystem.value = os
    const candidates = release.value.assets.filter((asset) => asset.os === os)
    // User-agent strings do not reliably identify hardware architecture.
    if (!choose && candidates.length === 1 && candidates[0]!.architecture === 'unknown') {
      window.location.assign(`/api/amelia/${candidates[0]!.id}`)
    } else {
      emit('closeMenu')
      open.value = true
    }
  } catch {
    emit('closeMenu')
    open.value = true
    error.value = 'Le téléchargement est indisponible pour le moment. Réessayez plus tard.'
  } finally {
    pending.value = false
  }
}
defineExpose({ download, pending })
</script>
<template>
  <UiModal
    :open="open"
    title="Télécharger Amélia"
    eyebrow="Application de bureau"
    description="Retrouvez votre espace Morning sur votre ordinateur."
    size="xl"
    mobile-mode="sheet"
    @update:open="open = $event"
  >
    <template #header-leading
      ><Download
        :size="22"
        aria-hidden="true"
    /></template>
    <template
      v-if="release && !error"
      #header-meta
    >
      <div class="amelia-download-release">
        <UiBadge tone="accent">Version {{ release.version }}</UiBadge>
        <span v-if="releaseDate">Publiée le {{ releaseDate }}</span>
      </div>
    </template>
    <div
      class="amelia-download"
      :aria-busy="pending || undefined"
    >
      <UiEmptyState
        v-if="pending"
        title="Recherche de la dernière version"
        description="Les téléchargements seront disponibles dans un instant."
      >
        <template #icon><Download :size="24" /></template>
      </UiEmptyState>
      <UiEmptyState
        v-else-if="error"
        title="Téléchargement indisponible"
        :description="error"
        role="alert"
      >
        <template #icon><AlertCircle :size="24" /></template>
        <template #actions><UiButton @click="download(true)">Réessayer</UiButton></template>
      </UiEmptyState>
      <template v-else-if="platforms.length">
        <p class="amelia-download-intro">
          Choisissez le système et le processeur de votre ordinateur.
        </p>
        <UiGrid
          :columns="3"
          min-width="260px"
          gap="4"
          class="amelia-download-platforms"
        >
          <UiCard
            v-for="platform in platforms"
            :key="platform.os"
            class="amelia-download-platform"
            :selected="platform.os === operatingSystem"
            padding="md"
          >
            <div class="amelia-download-platform-header">
              <div class="amelia-download-platform-topline">
                <UiSurface
                  as="span"
                  padding="sm"
                  radius="sm"
                  :tone="platform.os === operatingSystem ? 'accent' : 'neutral'"
                  class="amelia-download-platform-icon"
                >
                  <component
                    :is="platform.icon"
                    :size="26"
                    aria-hidden="true"
                  />
                </UiSurface>
                <UiBadge
                  v-if="platform.os === operatingSystem"
                  tone="accent"
                  >Votre système</UiBadge
                >
              </div>
              <div class="amelia-download-platform-heading">
                <h3>{{ platform.name }}</h3>
                <span
                  >{{ platform.assets.length }}
                  {{
                    platform.assets.length > 1 ? 'versions disponibles' : 'version disponible'
                  }}</span
                >
              </div>
            </div>
            <ul class="amelia-download-assets">
              <UiSurface
                v-for="asset in platform.assets"
                :key="asset.id"
                as="li"
                padding="sm"
                radius="sm"
                class="amelia-download-asset"
              >
                <div class="amelia-download-asset-details">
                  <div class="amelia-download-asset-label">
                    <strong>{{ architectureLabel(asset) }}</strong
                    ><UiBadge>{{ asset.format.toUpperCase() }}</UiBadge>
                  </div>
                  <span
                    class="amelia-download-filename"
                    :title="asset.name"
                    >{{ asset.name }}</span
                  >
                </div>
                <UiLink
                  :href="`/api/amelia/${asset.id}`"
                  :variant="platform.os === operatingSystem ? 'solid' : 'outline'"
                  :tone="platform.os === operatingSystem ? 'accent' : 'neutral'"
                  :aria-label="`Télécharger Amélia pour ${platform.name}, ${architectureLabel(asset)}, ${asset.format.toUpperCase()}`"
                >
                  <template #leading
                    ><Download
                      :size="16"
                      aria-hidden="true" /></template
                  >Télécharger
                </UiLink>
              </UiSurface>
            </ul>
          </UiCard>
        </UiGrid>
      </template>
      <UiEmptyState
        v-else
        title="Aucun téléchargement disponible"
        description="Aucun fichier d’installation n’est proposé pour cette version. Réessayez un peu plus tard."
      >
        <template #icon><Download :size="24" /></template>
        <template #actions><UiButton @click="download(true)">Actualiser</UiButton></template>
      </UiEmptyState>
    </div>
    <template #footer>
      <p
        v-if="platforms.length && !pending && !error"
        class="amelia-download-help"
      >
        <Info
          :size="16"
          aria-hidden="true"
        /><span
          >Un doute sur le processeur ? Consultez les informations système de votre
          ordinateur.</span
        >
      </p>
      <UiButton
        variant="outline"
        tone="neutral"
        @click="open = false"
        >Fermer</UiButton
      >
    </template>
  </UiModal>
</template>

<style scoped lang="scss" src="~/assets/scss/sfc/components/dashboard/AmeliaDownload.scss"></style>
